import {type ButtonHTMLAttributes, type ReactNode, memo} from 'react'
import {cva, type VariantProps} from 'class-variance-authority'

import {cn} from '@/shared/lib/cn'

const iconButtonVariants = cva(
  'inline-flex items-center justify-center rounded-full cursor-pointer text-foreground backdrop-blur-[10px] transition-all duration-200 ease-out hover:scale-105 active:scale-95 disabled:opacity-50 disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-foreground/15 hover:bg-foreground/25',
        muted: 'bg-destructive hover:bg-destructive/80',
        active: 'bg-primary hover:bg-primary/90',
      },
      size: {
        small: "size-10 [&_svg:not([class*='size-'])]:size-[18px]",
        medium: "size-13 [&_svg:not([class*='size-'])]:size-6",
        large: "size-16 [&_svg:not([class*='size-'])]:size-7",
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'medium',
    },
  },
)

interface IconButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof iconButtonVariants> {
  children: ReactNode
  'aria-label': string
}

const IconButton = memo(function IconButton({
  children,
  variant = 'default',
  size = 'medium',
  className,
  ...props
}: IconButtonProps) {
  return (
    <button className={cn(iconButtonVariants({variant, size}), className)} {...props}>
      {children}
    </button>
  )
})

export {IconButton, iconButtonVariants}
